import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SG_GET_ACTIONS } from 'constants/actions';
import PageIntro from '../components/PageIntro';

export default function ActionsList() {
  const dispatch = useDispatch();
  const actionsState = useSelector((s) => s.action_actions || {});
  const actions = useMemo(() => actionsState?.actions || [], [actionsState]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const q = `page=${page}`;
    setLoading(true);
    dispatch({ type: SG_GET_ACTIONS, payload: q });
  }, [dispatch, page]);

  // clear loading when actions arrive
  useEffect(() => {
    if (actions && (actions.results || actions.length >= 0 || actions.count)) {
      setLoading(false);
    }
  }, [actions]);

  const list = actions?.results || (Array.isArray(actions) ? actions : []);
  const count = actions?.count ?? (Array.isArray(actions) ? actions.length : undefined);
  const pageSize = actions?.page_size ?? '-';

  return (
    <div>
      <PageIntro
        title="Actions"
        description="Actions are tasks created by managers during the planning phase. They track work items, status and progress over time."
        bullets={[
          'Where data comes from: created by managers (via planning events) or admin imports.',
          'What to expect: status, progress %, and updates (check-ins).',
          'Next steps: review actions, assign owners, and monitor check-in updates.'
        ]}
      />

      <div style={{ marginTop: 12 }}>
        {loading ? <div style={{ color: '#666' }}>Loading actions...</div> : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {list.length ? (
            list.map((a) => {
              const overdue = (typeof a.days_until_deadline === 'number') && a.days_until_deadline < 0;
              const deadlineColor = overdue ? '#dc2626' : (a.days_until_deadline === 0 ? '#f59e0b' : '#16a34a');
              const statusColor = (a.status && typeof a.status === 'string') ? (a.status.startsWith('5') ? '#0b5fff' : '#16a34a') : '#9ca3af';
              const progress = (a.progress_percentage != null) ? Math.max(0, Math.min(100, a.progress_percentage)) : 0;

              return (
                <div key={a.id} style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title || a.name || '-'}</div>
                        <div style={{ color: '#9ca3af', fontSize: 12 }}>#{(a.id || '').slice(0, 8)}</div>
                        <div style={{ padding: '4px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, color: '#374151', fontWeight: 700 }}>{a.priority || '-'}</div>
                        <div style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>{a.campaign_name ? `campaign: ${a.campaign_name}` : ''}</div>
                      </div>

                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', color: '#6b7280', marginTop: 6, fontSize: 13 }}>
                        <div>Org: <strong style={{ color: '#374151' }}>{a.organization?.name ?? '-'}</strong></div>
                        <div>Manager: <strong style={{ color: '#374151' }}>{a.manager?.first_name ? `${a.manager.first_name} ${a.manager.last_name || ''}` : '-'}</strong></div>
                        <div>Assignees: <strong style={{ color: '#374151' }}>{a.assignees_count ?? 0}</strong></div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ padding: '6px 10px', borderRadius: 999, border: `1px solid ${statusColor}`, color: statusColor, fontWeight: 700 }}>{a.status || 'unknown'}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{new Date(a.created_at).toLocaleString()}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>#{a.id}</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {a.id && a.id !== 'undefined' ? (
                            <Link to={`${a.id}`} style={{ textDecoration: 'none' }}>
                              <div style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 6, border: '1px solid #e6edf3', background: '#fff', display: 'inline-block' }}>View</div>
                            </Link>
                          ) : (
                            <div style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #f3f4f6', color: '#9ca3af' }}>No id</div>
                          )}
                          <button
                            onClick={() => {
                              const fullId = a.id || '';
                              if (!fullId) return;
                              if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                                navigator.clipboard.writeText(fullId).then(() => setCopiedId(fullId));
                              } else {
                                const ta = document.createElement('textarea');
                                ta.value = fullId;
                                document.body.appendChild(ta);
                                ta.select();
                                document.execCommand('copy');
                                document.body.removeChild(ta);
                                setCopiedId(fullId);
                              }
                              setTimeout(() => setCopiedId(null), 1500);
                            }}
                            style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 6, border: '1px solid #e6edf3', background: copiedId === a.id ? '#0b5fff' : '#fff', color: copiedId === a.id ? '#fff' : '#111' }}
                          >
                            {copiedId === a.id ? 'Copied' : 'Copy id'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ height: 10, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${progress}%`, height: '100%', background: progress >= 100 ? '#059669' : '#0b5fff' }} />
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>Progress: <strong style={{ color: '#111' }}>{progress}%</strong> — Last update: {a.last_progress_update ? new Date(a.last_progress_update).toLocaleString() : '-'}</div>
                    </div>

                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>Deadline: <strong style={{ color: '#111' }}>{a.time_bound_deadline ? new Date(a.time_bound_deadline).toLocaleDateString() : '-'}</strong></div>
                      <div style={{ padding: '6px 8px', borderRadius: 6, background: '#fff', border: `1px solid ${deadlineColor}`, color: deadlineColor, fontWeight: 700 }}>{a.days_until_deadline ?? '-'}</div>
                    </div>

                    {/* details are shown on the dedicated route when you click View */}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: 18, border: '1px dashed #eee', borderRadius: 8, color: '#666' }}>No actions</div>
          )}
        </div>

        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => { setLoading(true); setPage(Math.max(1, page - 1)); }} disabled={loading || !actions?.previous || page <= 1}>Previous</button>
          <span>Page {page}{count ? ` of ${Math.ceil(count / (actions?.page_size || 50))}` : ''}</span>
          <button onClick={() => { setLoading(true); setPage(page + 1); }} disabled={loading || !actions?.next}>Next</button>

          <div style={{ marginLeft: 'auto' }}>
            Total: <strong>{count ?? '-'}</strong>
            <span style={{ marginLeft: 12 }}>Page size: <strong>{pageSize}</strong></span>
          </div>
        </div>

        {/* details are handled by nested details route; no inline global block here */}
      </div>
    </div>
  );
}
